import { useEffect, useState, useCallback, useRef } from 'react'
import { downloadMedia } from '../utils/media'
import { getComments as apiGetComments, addComment as apiAddComment, deleteComment as apiDeleteComment, getLikes as apiGetLikes, toggleCommentLike as apiToggleCommentLike } from '../utils/api'
import { C, fontStack } from '../constant/theme'

// Full-screen image/video viewer with social panel (likes + comments).
// Desktop: image left, panel right. Mobile: image top, panel below.
export default function Lightbox({
  items,
  index,
  onClose,
  onNav,
  viewsByMedia,
  slug,
  socialData,      // { [mediaId]: { count, likedByMe } }
  commentCounts,   // { [mediaId]: count }
  onToggleLike,
  onCommentAdded,
  onCommentDeleted,
}) {
  const open = index != null && items[index]

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 680)
  const [comments, setComments] = useState([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentInput, setCommentInput] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [showLikers, setShowLikers] = useState(false)
  const [likers, setLikers] = useState([])
  const [likersLoading, setLikersLoading] = useState(false)
  const commentsEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 680)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  // Load comments whenever the active item changes.
  useEffect(() => {
    if (!open || !slug) return
    const mediaId = items[index]._id
    let active = true
    setComments([])
    setCommentsLoading(true)
    setShowLikers(false)
    apiGetComments(slug, mediaId)
      .then((c) => active && setComments(c))
      .catch(() => {})
      .finally(() => active && setCommentsLoading(false))
    return () => { active = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, slug])

  const handleKey = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') onNav(1)
      if (e.key === 'ArrowLeft') onNav(-1)
    },
    [onClose, onNav],
  )

  useEffect(() => {
    if (!open) return
    window.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [open, handleKey])

  if (!open) return null
  const item = items[index]
  const likeData = (socialData && socialData[item._id]) || { count: 0, likedByMe: false }
  const commentCount = (commentCounts && commentCounts[item._id]) || 0

  const onDownload = async (e) => {
    e.stopPropagation()
    try { await downloadMedia(item) }
    catch { window.dispatchEvent(new CustomEvent('api-toast', { detail: { message: 'Download failed — check your connection.' } })) }
  }

  const handleLike = () => {
    if (onToggleLike) onToggleLike(item._id)
  }

  const handleOpenLikers = async () => {
    if (!slug) return
    setShowLikers(true)
    setLikersLoading(true)
    try {
      const res = await apiGetLikes(slug, item._id)
      setLikers(res.likers || [])
    } catch {
      setLikers([])
    } finally {
      setLikersLoading(false)
    }
  }

  const handleSubmitComment = async (e) => {
    e.preventDefault()
    const text = commentInput.trim()
    if (!text || submitting || !slug) return
    setSubmitting(true)
    try {
      const comment = await apiAddComment(slug, item._id, text)
      setComments((c) => [...c, comment])
      setCommentInput('')
      if (onCommentAdded) onCommentAdded(item._id)
      // Scroll to bottom after adding
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50)
    } catch {}
    finally { setSubmitting(false) }
  }

  const handleDeleteComment = async (commentId) => {
    if (!slug) return
    try {
      await apiDeleteComment(slug, item._id, commentId)
      setComments((c) => c.filter((x) => String(x._id) !== String(commentId)))
      if (onCommentDeleted) onCommentDeleted(item._id)
    } catch {}
  }

  const handleToggleCommentLike = async (commentId) => {
    if (!slug) return
    const prev = comments.find((c) => String(c._id) === String(commentId))
    if (!prev) return
    // Optimistic update
    setComments((cs) => cs.map((c) =>
      String(c._id) === String(commentId)
        ? { ...c, likedByMe: !c.likedByMe, likeCount: c.likedByMe ? Math.max((c.likeCount || 0) - 1, 0) : (c.likeCount || 0) + 1 }
        : c
    ))
    try {
      const res = await apiToggleCommentLike(slug, item._id, commentId)
      setComments((cs) => cs.map((c) =>
        String(c._id) === String(commentId) ? { ...c, likedByMe: res.liked, likeCount: res.count } : c
      ))
    } catch {
      // Revert on failure
      setComments((cs) => cs.map((c) =>
        String(c._id) === String(commentId) ? { ...c, likedByMe: prev.likedByMe, likeCount: prev.likeCount } : c
      ))
    }
  }

  // ── Layout ──
  const backdrop = isMobile ? S.backdropMobile : S.backdropDesktop

  const imageArea = (
    <div style={isMobile ? S.imageAreaMobile : S.imageAreaDesktop}>
      <button style={{ ...S.nav, left: isMobile ? 8 : 16 }} onClick={(e) => { e.stopPropagation(); onNav(-1) }}>‹</button>
      {item.type === 'video' ? (
        <video src={item.url} style={isMobile ? S.mediaMobile : S.mediaDesktop} controls autoPlay />
      ) : (
        <img src={item.url} alt={item.caption || ''} style={isMobile ? S.mediaMobile : S.mediaDesktop} />
      )}
      <button style={{ ...S.nav, right: isMobile ? 8 : 16 }} onClick={(e) => { e.stopPropagation(); onNav(1) }}>›</button>
      <div style={S.counter}>{index + 1} / {items.length}</div>
    </div>
  )

  const socialPanel = (
    <div style={isMobile ? S.panelMobile : S.panelDesktop} onClick={(e) => e.stopPropagation()}>
      {/* Panel header */}
      <div style={S.panelHeader}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={S.panelPoster}>
            {item.postedBy ? item.postedBy : <span style={S.panelPosterEmpty}>Unknown</span>}
          </div>
          <div style={S.panelViews}>
            👁 {(viewsByMedia && viewsByMedia[item._id]) || 0} views
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          <button style={S.iconBtn} title="Download" onClick={onDownload}>⬇</button>
          <button style={S.iconBtn} title="Close" onClick={onClose}>✕</button>
        </div>
      </div>

      {item.caption && <div style={S.caption}>{item.caption}</div>}

      {/* Likes row */}
      <div style={S.likesRow}>
        <button
          style={{ ...S.likeBtn, color: likeData.likedByMe ? '#e0245e' : 'rgba(255,255,255,0.75)' }}
          onClick={handleLike}
          title={likeData.likedByMe ? 'Unlike' : 'Like'}
        >
          <span style={{ fontSize: 22 }}>{likeData.likedByMe ? '❤️' : '🤍'}</span>
        </button>
        {likeData.count > 0 ? (
          <button style={S.likesCountBtn} onClick={handleOpenLikers}>
            {likeData.count} {likeData.count === 1 ? 'like' : 'likes'}
          </button>
        ) : (
          <span style={S.likesZero}>Be the first to like this</span>
        )}
      </div>

      <div style={S.divider} />

      {/* Comments list */}
      <div style={isMobile ? S.commentsAreaMobile : S.commentsAreaDesktop}>
        {commentsLoading ? (
          <div style={S.commentsEmpty}>Loading…</div>
        ) : comments.length === 0 ? (
          <div style={S.commentsEmpty}>No comments yet. Be the first!</div>
        ) : (
          <>
            {comments.map((c) => (
              <div key={String(c._id)} style={S.commentRow}>
                <div style={S.commentAvatar}>{(c.userName || '?').charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.commentMeta}>
                    <span style={S.commentName}>{c.userName || 'Unknown'}</span>
                    <span style={S.commentTime}>{formatTime(c.createdAt)}</span>
                  </div>
                  <div style={S.commentText}>{c.text}</div>
                  {/* Comment like button */}
                  <div style={S.commentActions}>
                    <button
                      style={{ ...S.commentLikeBtn, color: c.likedByMe ? '#e0245e' : TEXT_SOFT }}
                      onClick={() => handleToggleCommentLike(c._id)}
                      title={c.likedByMe ? 'Unlike' : 'Like comment'}
                    >
                      {c.likedByMe ? '❤️' : '🤍'}
                      {c.likeCount > 0 && <span style={S.commentLikeCount}>{c.likeCount}</span>}
                    </button>
                  </div>
                </div>
                {c.isOwn && (
                  <button style={S.deleteCommentBtn} title="Delete comment" onClick={() => handleDeleteComment(c._id)}>✕</button>
                )}
              </div>
            ))}
            <div ref={commentsEndRef} />
          </>
        )}
      </div>

      {/* Comment input */}
      <form style={S.commentForm} onSubmit={handleSubmitComment}>
        <input
          ref={inputRef}
          style={S.commentInput}
          placeholder="Add a comment…"
          value={commentInput}
          onChange={(e) => setCommentInput(e.target.value)}
          maxLength={2000}
          autoComplete="off"
        />
        <button
          type="submit"
          style={{ ...S.postBtn, opacity: commentInput.trim() && !submitting ? 1 : 0.4 }}
          disabled={!commentInput.trim() || submitting}
        >
          Post
        </button>
      </form>
    </div>
  )

  return (
    <div style={backdrop} onClick={onClose}>
      {isMobile ? (
        <>
          {imageArea}
          {socialPanel}
        </>
      ) : (
        <div style={S.desktopInner} onClick={(e) => e.stopPropagation()}>
          {imageArea}
          {socialPanel}
        </div>
      )}

      {/* Likers modal */}
      {showLikers && (
        <div style={S.likersBackdrop} onClick={() => setShowLikers(false)}>
          <div style={S.likersCard} onClick={(e) => e.stopPropagation()}>
            <div style={S.likersHead}>
              <span style={S.likersTitle}>Liked by</span>
              <button style={S.likersClose} onClick={() => setShowLikers(false)}>✕</button>
            </div>
            <div style={S.likersBody}>
              {likersLoading ? (
                <div style={S.likersEmpty}>Loading…</div>
              ) : likers.length === 0 ? (
                <div style={S.likersEmpty}>No likes yet.</div>
              ) : (
                likers.map((l, i) => (
                  <div key={i} style={S.likersRow}>
                    <div style={S.likersAvatar}>{(l.userName || '?').charAt(0).toUpperCase()}</div>
                    <span style={S.likersName}>{l.userName || 'Unknown'}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function formatTime(ms) {
  try {
    const now = Date.now()
    const diff = now - ms
    if (diff < 60000) return 'just now'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
    return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch { return '' }
}

const PANEL_BG = '#0f1923'
const PANEL_BORDER = 'rgba(255,255,255,0.08)'
const TEXT_PRIMARY = 'rgba(255,255,255,0.92)'
const TEXT_SOFT = 'rgba(255,255,255,0.55)'

const S = {
  // Backdrops
  backdropDesktop: {
    position: 'fixed', inset: 0, background: 'rgba(4,10,18,0.96)', zIndex: 10000,
    display: 'flex', alignItems: 'stretch', justifyContent: 'center', fontFamily: fontStack,
  },
  backdropMobile: {
    position: 'fixed', inset: 0, background: '#040a12', zIndex: 10000,
    display: 'flex', flexDirection: 'column', fontFamily: fontStack, overflow: 'hidden',
  },

  // Desktop: full-height flex row capped at 1200px
  desktopInner: {
    display: 'flex', flexDirection: 'row', width: '100%', maxWidth: 1200, margin: '0 auto',
  },

  // Image areas
  imageAreaDesktop: {
    flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#000', minWidth: 0,
  },
  imageAreaMobile: {
    position: 'relative', height: '46vh', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000',
  },

  // Media
  mediaDesktop: { maxWidth: '100%', maxHeight: '100vh', objectFit: 'contain' },
  mediaMobile: { maxWidth: '100%', maxHeight: '46vh', objectFit: 'contain' },

  nav: {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    width: 44, height: 44, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 26,
    cursor: 'pointer', zIndex: 2, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  counter: {
    position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600,
    background: 'rgba(0,0,0,0.5)', padding: '3px 10px', borderRadius: 20,
  },

  // Social panel
  panelDesktop: {
    width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column',
    background: PANEL_BG, borderLeft: `1px solid ${PANEL_BORDER}`, overflow: 'hidden',
  },
  panelMobile: {
    flex: 1, display: 'flex', flexDirection: 'column', background: PANEL_BG,
    borderTop: `1px solid ${PANEL_BORDER}`, overflow: 'hidden',
  },

  panelHeader: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 16px', borderBottom: `1px solid ${PANEL_BORDER}`, flexShrink: 0,
  },
  panelPoster: { fontSize: 14, fontWeight: 700, color: TEXT_PRIMARY, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  panelPosterEmpty: { color: TEXT_SOFT, fontStyle: 'italic', fontWeight: 400 },
  panelViews: { fontSize: 12, color: TEXT_SOFT, marginTop: 2 },

  iconBtn: {
    width: 34, height: 34, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 15, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  caption: {
    fontSize: 13.5, color: TEXT_PRIMARY, padding: '10px 16px 0',
    lineHeight: 1.55, flexShrink: 0,
  },

  // Likes
  likesRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 16px', flexShrink: 0,
  },
  likeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    display: 'flex', alignItems: 'center', transition: 'transform .12s',
  },
  likesCountBtn: {
    background: 'none', border: 'none', color: TEXT_PRIMARY, fontSize: 14,
    fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0,
    textDecoration: 'underline', textUnderlineOffset: 3,
  },
  likesZero: { fontSize: 13, color: TEXT_SOFT },

  divider: { height: 1, background: PANEL_BORDER, margin: '0 16px', flexShrink: 0 },

  // Comments
  commentsAreaDesktop: {
    flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 14,
  },
  commentsAreaMobile: {
    flex: 1, overflowY: 'auto', padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 12,
    minHeight: 0,
  },
  commentsEmpty: { fontSize: 13, color: TEXT_SOFT, textAlign: 'center', padding: '20px 0' },

  commentRow: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  commentAvatar: {
    width: 30, height: 30, borderRadius: '50%', background: C.navy2, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700, flexShrink: 0,
  },
  commentMeta: { display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 3 },
  commentName: { fontSize: 13, fontWeight: 700, color: TEXT_PRIMARY },
  commentTime: { fontSize: 11, color: TEXT_SOFT },
  commentText: { fontSize: 13.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.5, wordBreak: 'break-word' },
  deleteCommentBtn: {
    background: 'none', border: 'none', color: TEXT_SOFT, fontSize: 12,
    cursor: 'pointer', padding: '2px 4px', flexShrink: 0, lineHeight: 1,
  },
  commentActions: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 },
  commentLikeBtn: {
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1,
  },
  commentLikeCount: { fontSize: 12, color: TEXT_SOFT, fontWeight: 600 },

  // Comment input
  commentForm: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 14px 12px', borderTop: `1px solid ${PANEL_BORDER}`, flexShrink: 0,
  },
  commentInput: {
    flex: 1, background: 'rgba(255,255,255,0.08)', border: `1px solid ${PANEL_BORDER}`,
    borderRadius: 22, padding: '9px 14px', color: TEXT_PRIMARY, fontSize: 13.5,
    outline: 'none', fontFamily: 'inherit',
  },
  postBtn: {
    background: '#1d6fdb', border: 'none', color: '#fff', fontWeight: 700,
    fontSize: 13, borderRadius: 22, padding: '9px 16px', cursor: 'pointer',
    fontFamily: 'inherit', flexShrink: 0, transition: 'opacity .15s',
  },

  // Likers modal (sits on top of lightbox)
  likersBackdrop: {
    position: 'absolute', inset: 0, background: 'rgba(4,10,18,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10001, padding: 20,
  },
  likersCard: {
    background: '#1a2535', borderRadius: 14, width: '100%', maxWidth: 340,
    maxHeight: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    border: `1px solid ${PANEL_BORDER}`, boxShadow: '0 8px 40px rgba(0,0,0,.6)',
  },
  likersHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 18px', borderBottom: `1px solid ${PANEL_BORDER}`,
  },
  likersTitle: { fontSize: 15, fontWeight: 800, color: TEXT_PRIMARY },
  likersClose: {
    width: 28, height: 28, borderRadius: '50%', border: 'none',
    background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: 13,
  },
  likersBody: { overflowY: 'auto', padding: '8px 18px 14px', display: 'flex', flexDirection: 'column', gap: 2 },
  likersEmpty: { fontSize: 13, color: TEXT_SOFT, textAlign: 'center', padding: '16px 0' },
  likersRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${PANEL_BORDER}` },
  likersAvatar: {
    width: 36, height: 36, borderRadius: '50%', background: C.navy2, color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0,
  },
  likersName: { fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY },
}
